import {
  Accordion,
  Alert,
  Group,
  MultiSelect,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { useRef, useState } from 'react';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import useRetrieveAutomationVersion from '@/hooks/api/automationVersions/useRetrieveAutomationVersion.ts';
import useRetrieveRunTestOutcome from '@/hooks/api/runs/useRetrieveRunTestOutcome.ts';
import { getHttpErrorMessage } from '@/services/httpRetry.ts';
import type { AutomationStep } from '@/types/automationSteps.ts';
import type {
  AutomationVersionTestFixtureBinding,
  AutomationVersionTestSuite,
  AutomationVersionTestSuiteCase,
} from '@/types/automations.ts';
import { getAllStepChildren, isStepWithChildren } from '@/utils/stepUtils.ts';
import { get_automation_version_test_fixture_binding_url } from '@/utils/urls.ts';
import StyledButton from '@/components/StyledButton/StyledButton.tsx';
import RunTestOutcomePanel from '@/pages/Run/components/RunTestOutcomePanel.tsx';
import { EMPTY_SCHEDULER_TEST_INTENT } from './versionTestSuiteAuthoring.ts';
import TestPlanAnalysisSummary from './TestPlanAnalysisSummary.tsx';
import type { TestPlanAnalysis, TestPlanIssue } from '@/types/testPlanAnalysis.ts';

type Plan = {
  eligible: boolean;
  plan_token?: string | null;
  execution_slice?: TestPlanAnalysis & {
    target_step_ids: string[];
    scheduled_step_ids: string[];
    effective_fresh_step_ids?: string[];
    blocked_effect_step_ids: string[];
  };
  issues?: TestPlanIssue[];
};
type PendingLaunch = { plan: Plan; requestId: string; runId?: string };
type Props = {
  automationId: string;
  versionId: string;
  fences?: AutomationVersionTestSuite;
  fixtureOptions: { value: string; label: string; disabled?: boolean }[];
  canScaffold: boolean;
  onScaffold: (testCase: AutomationVersionTestSuiteCase) => void;
};

function flattenSteps(steps: AutomationStep[]): AutomationStep[] {
  return steps.flatMap((step) => [
    step,
    ...(isStepWithChildren(step) ? flattenSteps(getAllStepChildren(step)) : []),
  ]);
}

function readPending(key: string): PendingLaunch | null {
  try {
    const value = JSON.parse(sessionStorage.getItem(key) ?? 'null');
    return value?.plan?.eligible === true &&
      typeof value.plan.plan_token === 'string' &&
      typeof value.requestId === 'string'
      ? value
      : null;
  } catch {
    return null;
  }
}

function storePending(key: string, launch: PendingLaunch | null): boolean {
  try {
    if (launch) sessionStorage.setItem(key, JSON.stringify(launch));
    else sessionStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

export default function DiagnosticRunPanel({
  automationId,
  versionId,
  fences,
  fixtureOptions,
  canScaffold,
  onScaffold,
}: Props) {
  const apiClient = useApiClient();
  const versionQuery = useRetrieveAutomationVersion(versionId);
  const storageKey = `diagnostic-launch:${automationId}:${versionId}`;
  const [pending, setPending] = useState<PendingLaunch | null>(() => readPending(storageKey));
  const [storageUnavailable, setStorageUnavailable] = useState(false);
  const [name, setName] = useState('Diagnostic check');
  const [fixtureId, setFixtureId] = useState<string | null>(null);
  const [targets, setTargets] = useState<string[]>([]);
  const [assertionsText, setAssertionsText] = useState(
    '{"schema_version":"builder_test_assertion_suite.v1","assertions":[]}',
  );
  const [advancedIntentText, setAdvancedIntentText] = useState('');
  const [plan, setPlan] = useState<Plan | null>(pending?.plan ?? null);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [runId, setRunId] = useState<string | null>(pending?.runId ?? null);
  const [caseId, setCaseId] = useState('');
  const [required, setRequired] = useState(false);
  const outcome = useRetrieveRunTestOutcome(runId ?? undefined);
  const baseUrl = `/automations/${automationId}/automation-versions/${versionId}/diagnostic-tests`;
  const locked = busy || pending !== null;
  const steps = flattenSteps(versionQuery.data?.steps ?? []);
  const stepOptions = steps.map((step) => ({
    value: step.step_id,
    label: step.name || step.step_id,
  }));
  const invalidate = () => {
    setPlan(null);
    setError(null);
  };

  const prepare = async () => {
    if (inFlight.current || pending) return;
    inFlight.current = true;
    setBusy(true);
    invalidate();
    try {
      if (
        !fixtureId ||
        !targets.length ||
        !fences?.latest_version_id ||
        fences.head_revision == null
      ) {
        throw new Error(
          'Select test inputs and steps, and refresh the current version information.',
        );
      }
      const binding = (
        await apiClient.get<AutomationVersionTestFixtureBinding>(
          get_automation_version_test_fixture_binding_url(automationId, versionId, fixtureId),
        )
      ).data;
      if (!binding.compatible)
        throw new Error(
          binding.issue?.message || 'Test inputs are incompatible with this version.',
        );
      const advanced = advancedIntentText.trim() ? JSON.parse(advancedIntentText) : {};
      if (!advanced || typeof advanced !== 'object' || Array.isArray(advanced))
        throw new Error('Advanced expectations must be a JSON object.');
      const response = await apiClient.post<Plan>(`${baseUrl}/plan`, {
        automation_version_id: versionId,
        name,
        fixture_id: fixtureId,
        intent: {
          ...EMPTY_SCHEDULER_TEST_INTENT,
          ...advanced,
          target_step_ids: targets,
          fixture_substitutions: binding.fixture_substitutions,
        },
        assertion_suite: JSON.parse(assertionsText),
        expected_latest_version_id: fences.latest_version_id,
        expected_published_version_id: fences.published_version_id ?? null,
        expected_head_revision: fences.head_revision,
      });
      setPlan(response.data);
    } catch (cause) {
      setError(
        getHttpErrorMessage(cause) ||
          (cause instanceof Error ? cause.message : 'Could not prepare diagnostic.'),
      );
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const launch = async () => {
    const reviewed = pending?.plan ?? plan;
    if (inFlight.current || !reviewed?.eligible || !reviewed.plan_token) return;
    const attempt = pending ?? { plan: reviewed, requestId: crypto.randomUUID() };
    inFlight.current = true;
    setBusy(true);
    setError(null);
    if (!pending) setRunId(null);
    setPending(attempt);
    setStorageUnavailable(!storePending(storageKey, attempt));
    try {
      const response = await apiClient.post(`${baseUrl}/runs/from-plan`, {
        plan_token: reviewed.plan_token,
        request_id: attempt.requestId,
        confirm_run: true,
      });
      const createdId =
        typeof response.data?.id === 'string' && response.data.id.trim()
          ? response.data.id
          : undefined;
      if (createdId) setRunId(createdId);
      if (
        !createdId ||
        !response.data?.liveness ||
        response.data.liveness.reason_code === 'builder_test_launch_preparation_unobserved'
      ) {
        const unconfirmed = { ...attempt, runId: createdId ?? attempt.runId };
        setPending(unconfirmed);
        setStorageUnavailable(!storePending(storageKey, unconfirmed));
        setError(
          'The launch handoff is unconfirmed. Inspect the run or retry the same request before another launch.',
        );
      } else {
        setPending(null);
        setStorageUnavailable(!storePending(storageKey, null));
        setPlan(null);
      }
    } catch (cause) {
      setError(
        `${getHttpErrorMessage(cause) || 'Could not confirm launch.'} Request ID: ${attempt.requestId}. Retry this request or inspect run history before starting another.`,
      );
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const scaffold = async () => {
    if (!runId || inFlight.current || !canScaffold) return;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      const response = await apiClient.post(
        `/automations/${automationId}/runs/${runId}/diagnostic-regression-case`,
        {
          automation_version_id: versionId,
          case_id: caseId.trim() || `diagnostic-${runId}`,
          required,
        },
      );
      onScaffold(response.data.case);
    } catch (cause) {
      setError(getHttpErrorMessage(cause) || 'Could not prepare regression case.');
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <Stack gap="sm">
      <Alert color="blue" title="Diagnostic run">
        Checks this exact version with external actions blocked. Definition and results stay with
        the run; the version and regression suite stay unchanged.
      </Alert>
      <Text size="xs" c="dimmed">
        Version: {versionId}. Record new test inputs in the shared inputs section.
      </Text>
      <TextInput
        label="Diagnostic name"
        value={name}
        disabled={locked}
        onChange={(event) => {
          setName(event.currentTarget.value);
          invalidate();
        }}
      />
      <Select
        label="Test inputs"
        data={fixtureOptions}
        value={fixtureId}
        searchable
        disabled={locked}
        onChange={(value) => {
          setFixtureId(value);
          invalidate();
        }}
      />
      <MultiSelect
        label="Steps to check"
        data={stepOptions}
        value={targets}
        searchable
        disabled={locked || versionQuery.isPending}
        onChange={(value) => {
          setTargets(value);
          invalidate();
        }}
      />
      {versionQuery.isError && (
        <Text c="red" size="sm">
          Could not load steps for this version.
        </Text>
      )}
      <Textarea
        label="Expected-result checks (JSON)"
        description="Empty assertions leave expected results unchecked. Checks require fresh output by default. JSON value paths use rows.0.amount; omit JSONPath roots ($) and brackets."
        value={assertionsText}
        minRows={4}
        disabled={locked}
        onChange={(event) => {
          setAssertionsText(event.currentTarget.value);
          invalidate();
        }}
      />
      <Accordion>
        <Accordion.Item value="expectations">
          <Accordion.Control>Advanced options</Accordion.Control>
          <Accordion.Panel>
            <Textarea
              label="Route and failure expectations (optional JSON)"
              description="Use supported scheduler intent fields. Selected steps and test inputs take precedence."
              value={advancedIntentText}
              minRows={2}
              disabled={locked}
              onChange={(event) => {
                setAdvancedIntentText(event.currentTarget.value);
                invalidate();
              }}
            />
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
      {error && (
        <Alert color="red" title="Diagnostic needs attention">
          {error}
        </Alert>
      )}
      <StyledButton
        onClick={() => void prepare()}
        loading={busy && pending === null}
        disabled={locked || !fixtureId || !targets.length || !name.trim()}
      >
        Prepare diagnostic
      </StyledButton>
      {plan && (
        <Alert
          color={plan.eligible ? 'blue' : 'red'}
          title={plan.eligible ? 'Ready to run — no execution yet' : 'Diagnostic cannot run'}
        >
          <Stack gap="xs">
            <TestPlanAnalysisSummary analysis={plan.execution_slice} issues={plan.issues} />
            {plan.execution_slice ? (
              <>
                <Text size="sm">
                  External-action steps blocked: {plan.execution_slice.blocked_effect_step_ids.length}.
                </Text>
                <Text size="sm">
                  Scheduled steps: {plan.execution_slice.scheduled_step_ids.join(', ') || 'None'}.
                </Text>
                <Text size="sm">
                  Fresh steps:{' '}
                  {(
                    plan.execution_slice.effective_fresh_step_ids ??
                    plan.execution_slice.target_step_ids
                  )?.join(', ') || 'None'}
                  . Compatible upstream results may be reused.
                </Text>
              </>
            ) : <Text size="sm">Execution scope is unavailable for this plan.</Text>}
            {plan.issues?.map((issue, index) => (
              <Text key={index} size="sm">
                {issue.message}
              </Text>
            ))}
            {plan.eligible && plan.plan_token && (
              <StyledButton onClick={() => void launch()} loading={busy && pending !== null}>
                {pending
                  ? 'Retry same diagnostic launch'
                  : 'Run diagnostic — external actions blocked'}
              </StyledButton>
            )}
          </Stack>
        </Alert>
      )}
      {pending && (
        <Alert color="yellow" title="Earlier launch is unconfirmed">
          <Text size="sm">Request ID: {pending.requestId}. A run may already exist.</Text>
          <Text size="sm">
            Include Safe execution in run history filters; default history shows operational runs.
          </Text>
          <StyledButton
            variant="subtle"
            disabled={busy}
            onClick={() => {
              setPending(null);
              setStorageUnavailable(!storePending(storageKey, null));
              setPlan(null);
            }}
          >
            Clear pending launch after checking run history
          </StyledButton>
        </Alert>
      )}
      {storageUnavailable && (
        <Alert color="yellow" title="Launch recovery storage unavailable">
          Keep this panel open to retry with the same request. Before closing, record its request
          ID. After closing or reloading, check run history with Safe execution enabled before
          another launch.
        </Alert>
      )}
      {runId && (
        <Stack gap="sm">
          <Text component="a" href={`/automations/${automationId}/runs/${runId}`}>
            Inspect diagnostic run {runId}
          </Text>
          <RunTestOutcomePanel
            outcome={outcome.data}
            isLoading={outcome.isFetching}
            errorMessage={outcome.isError ? 'Refresh or inspect the run.' : null}
          />
          <StyledButton variant="subtle" onClick={() => void outcome.refetch()}>
            Refresh diagnostic result
          </StyledButton>
          <TextInput
            label="Regression case ID (optional)"
            value={caseId}
            onChange={(event) => setCaseId(event.currentTarget.value)}
          />
          <Switch
            label="Require this case for regression assurance"
            checked={required}
            onChange={(event) => setRequired(event.currentTarget.checked)}
          />
          <Group>
            <StyledButton
              variant="outline"
              onClick={() => void scaffold()}
              disabled={busy || !canScaffold}
            >
              Prepare saved regression case
            </StyledButton>
            <Text size="xs" c="dimmed">
              Preparing does not save. Save in the regression tab to create a draft, then run the
              case afresh.
            </Text>
          </Group>
        </Stack>
      )}
    </Stack>
  );
}
